"use client"

import { useEffect, useState } from "react"
import { Bell, X } from "lucide-react"
import { FreeReminderForm } from "@/components/FreeReminderForm"
import { ResidencyWatcherForm } from "@/components/ResidencyWatcherForm"

type Props = {
  residencyId: string
  residencyName: string
  /** ID of the soonest open call, if any. Drives which form variant shows. */
  openCallId: string | null
}

/**
 * Mobile-only sticky bottom CTA on residency pages. Drops in for logged-out
 * visitors who scroll past the inline forms — captures the bounce-prone
 * mobile user who would otherwise leave without converting.
 *
 * Auto-hides on desktop (≥768px), once dismissed for the session, and after
 * the form is submitted. Pops up in a small bottom drawer when tapped.
 */
export function ResidencyMobileStickyCTA({ residencyId, residencyName, openCallId }: Props) {
  const [expanded, setExpanded] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [shouldShow, setShouldShow] = useState(false)

  useEffect(() => {
    // Show after the visitor scrolls ~600px (past the hero/about-the-fold).
    function onScroll() {
      if (window.scrollY > 600) {
        setShouldShow(true)
        window.removeEventListener("scroll", onScroll)
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    if (!expanded) return
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setExpanded(false)
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [expanded])

  if (dismissed || !shouldShow) return null

  return (
    <>
      {/* Collapsed bar */}
      {!expanded && (
        <div className="md:hidden fixed bottom-3 left-3 right-3 z-40 flex items-stretch gap-2 animate-in slide-in-from-bottom-2 fade-in duration-300">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 text-[14px] font-semibold bg-[var(--foreground)] text-white rounded-full shadow-lg active:scale-[0.98] transition-transform"
          >
            <Bell size={15} className="text-amber-300" />
            {openCallId ? "Remind me before this closes" : "Notify me when this opens"}
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            className="shrink-0 inline-flex items-center justify-center px-3 bg-white border border-[var(--border)] rounded-full shadow-lg active:scale-[0.98] transition-transform text-[var(--muted-foreground)]"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Expanded sheet */}
      {expanded && (
        <div className="md:hidden fixed inset-0 z-50 flex flex-col" role="dialog" aria-modal="true">
          <button
            type="button"
            onClick={() => setExpanded(false)}
            aria-label="Close"
            className="flex-1 bg-black/40 backdrop-blur-sm animate-fade-in"
          />
          <div className="bg-white rounded-t-2xl px-5 py-5 animate-slide-up-sheet shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <Bell size={14} className="text-amber-600" />
                <h3 className="text-[15px] font-semibold">
                  {openCallId ? "Remind me before this closes" : `Notify me when ${residencyName} opens a new call`}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setExpanded(false)}
                aria-label="Close"
                className="p-1 -mr-1 rounded-md hover:bg-[var(--secondary)] text-[var(--muted-foreground)]"
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-[12px] text-[var(--muted-foreground)] mb-3">
              One email, no account needed.{" "}
              <a href="/pro" className="underline hover:text-[var(--foreground)]">Saver</a> unlocks every residency.
            </p>
            {openCallId ? (
              <FreeReminderForm openCallId={openCallId} source="residency_page" compact />
            ) : (
              <ResidencyWatcherForm
                residencyId={residencyId}
                residencyName={residencyName}
                source="residency_page_no_call"
                compact
              />
            )}
          </div>
        </div>
      )}
    </>
  )
}
